import React, { useEffect, useState } from "react";
import {
	IconButton,
	Tooltip,
	Typography,
	Link,
	Button,
	Grid,
	LinearProgress,
	Box,
} from "@mui/material";
import { useSelector } from "react-redux";

import { CommentIcon } from "utility/icons";
import "components/Layout/Sidebar/WorkFlowApprovalStepsHistory/WorkFlowApprovalStepsHistory.css";
import { Shared } from "utility/shared";
import Emitter from "utility/emitter";
import ResponsiveDialog from "components/ResponsiveDialog";
import { RootState } from "redux/store";
import { IStepHistoryComment } from "models/StepHistoryDetail";
import {
	BudgetAllocationStepHistoryEmitter,
	IBudgetAllocationStepHistory,
} from "models/Budget/BudgetAllocationDetail";
import { BudgetAllocationService } from "services/budgetAllocation.service";
import {
	BudgetAllocationStepHistoryStatus,
	BudgetAllocationStepHistoryStatusId,
	BudgetModulePhase,
} from "utility/enums/budget-allocation";
import { CustomTooltip } from "components/CustomTooltip";
import { OPEN_BUDGET_ALLOCATION_STEP_HISTORY_DETAIL } from "utility/emitterEvents";
import { ActionType } from "utility/enums/action-type";
import { useNavigate } from "react-router-dom";
import { AppRoutings } from "utility/enums/app-routings";
import { Loading } from "components/Loading";

type Props = {
	budgetAllocationDetail: BudgetAllocationStepHistoryEmitter;
};

export const BudgetAllocationStepHistory: React.FC<Props> = ({
	budgetAllocationDetail,
}) => {
	const navigate = useNavigate();
	const userDetails = useSelector(
		(state: RootState) => state.authState.userDetails
	);
	const [stepHistoryList, setStepHistoryList] = useState<
		IBudgetAllocationStepHistory[]
	>([]);
	const [openInfoDialog, setOpenInfoDialog] = useState<{
		open: boolean;
		message: string;
	}>({ open: false, message: "" });
	const [activeStepIndex, setActiveStepIndex] = useState<number>(-1);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const isStepAssignedToUser = (step: IBudgetAllocationStepHistory) => {
		return step.assignToUsers && step.assignToUsers.length > 0
			? step.assignToUsers.findIndex((r) => r.userId === userDetails.userId) >
					-1
			: false;
	};

	const openBudgetAllocationStepHistoryDetail = (
		item: IBudgetAllocationStepHistory,
		actionType?: ActionType
	) => {
		Emitter.off(OPEN_BUDGET_ALLOCATION_STEP_HISTORY_DETAIL, {} as any);
		const tempActionType =
			actionType ||
			(isStepAssignedToUser(item) &&
			item.stepStatus === BudgetAllocationStepHistoryStatus.Ready
				? ActionType.Edit
				: ActionType.View);
		Emitter.emit(OPEN_BUDGET_ALLOCATION_STEP_HISTORY_DETAIL, {
			stepHistoryDetail: item,
			actionType: tempActionType,
			isStepAssignedToUser: isStepAssignedToUser(item),
		});
	};

	const setPrevStepAsSelected = (
		stepHistoryList: IBudgetAllocationStepHistory[],
		filteredList: IBudgetAllocationStepHistory[]
	) => {
		for (let i = 0; i < filteredList.length; i++) {
			const element = filteredList[i];
			if (
				isStepAssignedToUser(element) &&
				element.stepStatus !== BudgetAllocationStepHistoryStatus.Pending
			) {
				const index = stepHistoryList.findIndex(
					(r) =>
						r.budgetAllocationStepId === element.budgetAllocationStepId &&
						r.sequenceNumber === element.sequenceNumber
				);
				if (index > -1) {
					setActiveStepIndex(index);
					openBudgetAllocationStepHistoryDetail(stepHistoryList[index]);
					break;
				}
			}
			// if no prev step is assigned to user, set first step as selected
			if (filteredList.length - 1 === i) {
				setActiveStepIndex(0);
				openBudgetAllocationStepHistoryDetail(
					stepHistoryList[0],
					ActionType.View
				);
			}
		}
	};

	useEffect(() => {
		// get budget allocation Step History List
		if (budgetAllocationDetail.budgetAllocationId > -1) {
			setIsLoading(true);
			setActiveStepIndex(-1);
			BudgetAllocationService.getBudgetAllocationStepList(
				budgetAllocationDetail.budgetAllocationId
			)
				.then((result) => {
					setStepHistoryList(result);
					// Required to filter step list, if there are two steps with same budgetAllocationStepId, but with different status "Ready" and "Completed"
					const filteredStepList = result.filter(
						(r) =>
							r.budgetAllocationStepId ===
							budgetAllocationDetail.budgetAllocationStepId
					);
					const index = result.findIndex(
						(r) =>
							r.budgetAllocationStepId ===
								budgetAllocationDetail.budgetAllocationStepId &&
							(filteredStepList.length > 1
								? r.stepStatusId === BudgetAllocationStepHistoryStatusId.Ready
								: true)
					);
					if (index > -1) {
						if (isStepAssignedToUser(result[index])) {
							setActiveStepIndex(index);
							openBudgetAllocationStepHistoryDetail(
								result[index],
								result[index].stepStatus ===
									BudgetAllocationStepHistoryStatus.Ready
									? ActionType.Edit
									: ActionType.View
							);
						} else {
							setPrevStepAsSelected(
								result,
								JSON.parse(JSON.stringify(result)).slice(0, index).reverse()
							);
						}
					} else {
						setPrevStepAsSelected(
							result,
							JSON.parse(JSON.stringify(result)).reverse()
						);
					}
					const activeElement = document.getElementById("active");
					if (activeElement) {
						activeElement.scrollIntoView({
							block: "center",
							behavior: "smooth",
						});
					}
					setIsLoading(false);
				})
				.catch((err) => {
					Shared.showSnackbarMessage(
						"Error loading budget allocation step history list, please try again"
					);
					navigate(AppRoutings.BudgetAllocationSummaryGrid);
					setIsLoading(false);
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		budgetAllocationDetail.budgetAllocationId,
		budgetAllocationDetail.budgetAllocationStepId,
	]);

	const getClassName = (status: string) => {
		let classes: string = "steps-root ";
		if (status === BudgetAllocationStepHistoryStatus.Ready) {
			classes += "in-progress";
		}
		if (status === BudgetAllocationStepHistoryStatus.Completed) {
			classes += "complete";
		}
		if (status === BudgetAllocationStepHistoryStatus.Recalled) {
			classes += "recall";
		}
		if (status === BudgetAllocationStepHistoryStatus.ChangeRequested) {
			classes += "change-request";
		}
		if (status === BudgetAllocationStepHistoryStatus.Rejected) {
			classes += "reject";
		}
		if (status === BudgetAllocationStepHistoryStatus.Deleted) {
			classes += "delete";
		}
		if (status === BudgetAllocationStepHistoryStatus.Delegated) {
			classes += "delegate";
		}
		if (status === BudgetAllocationStepHistoryStatus.Escalated) {
			classes += "escalate";
		}
		if (status === BudgetAllocationStepHistoryStatus.Cancelled) {
			classes += "cancel";
		}
		return classes;
	};

	const handleStepDetailClick = (
		item: IBudgetAllocationStepHistory,
		index: number
	) => {
		if (activeStepIndex === index) {
			return;
		}
		if (
			item.stepStatus === BudgetAllocationStepHistoryStatus.Pending ||
			(!isStepAssignedToUser(item) &&
				(item.stepStatus === BudgetAllocationStepHistoryStatus.Ready ||
					item.modulePhaseId === BudgetModulePhase.DepartmentalAllocation))
		) {
			setOpenInfoDialog({
				open: true,
				message:
					!isStepAssignedToUser(item) &&
					item.modulePhaseId === BudgetModulePhase.DepartmentalAllocation
						? `You do not have permission to access the step ${item.stepNumber}.`
						: `The step selected ${item.stepNumber}, has not been completed and there is no information to display for the step.`,
			});
		} else {
			setActiveStepIndex(index);
			openBudgetAllocationStepHistoryDetail(item);
		}
	};

	return (
		<div className="history-panel">
			<div className="heading-part">
				<Typography variant="h4">
					Budget allocation approval Steps - History
				</Typography>
				{stepHistoryList.length > 0 && (
					<div className="stepper-wrapper">
						<Typography variant="caption">
							{`${stepHistoryList[0].stepCompletedInPercentage}% Completed`}
						</Typography>
						<Box sx={{ ml: 1, maxWidth: 400, flexGrow: 1 }}>
							<LinearProgress
								variant="determinate"
								value={stepHistoryList[0].stepCompletedInPercentage}
							/>
						</Box>
					</div>
				)}
			</div>
			<div className="steps-wrapper">
				{!stepHistoryList.length &&
					(isLoading ? (
						<Loading isWorkflowStepLoader={true} />
					) : (
						<Typography
							className="steps-root"
							variant="body1"
							justifyContent="center"
						>
							No Data Available
						</Typography>
					))}
				{stepHistoryList.length > 0 &&
					stepHistoryList.map(
						(item: IBudgetAllocationStepHistory, index: number) => (
							<Link
								key={index}
								onClick={(e) => {
									e.preventDefault();
									handleStepDetailClick(item, index);
								}}
								underline="none"
								className={`${getClassName(item.stepStatus)} ${
									index === activeStepIndex && "active"
								}`}
								style={{
									cursor: "pointer",
								}}
								id={index === activeStepIndex ? "active" : undefined}
							>
								<Tooltip title={item.stepStatus} placement="bottom">
									<div className="steps-icon">{item.stepNumber}</div>
								</Tooltip>
								<div className="step-content">
									<Typography variant="body2">{item.description}</Typography>
									<Typography variant="caption">
										{item.assignToUsers.map((user, index) => (
											<span
												key={index}
												className={
													user.isTaskCompleted ? "green-text fw-500" : ""
												}
											>{`${user.fullName}${
												index !== item.assignToUsers.length - 1 ? ", " : ""
											}`}</span>
										))}
									</Typography>
									<div className="step-content-middle">
										<div className="left-part">
											<Typography variant="caption" className="light">
												Duration
											</Typography>
											<Typography variant="caption">
												{item.duration || "-"}
											</Typography>
										</div>
										<div className="right-part">
											<Typography variant="caption" className="light">
												Avg. Duration
											</Typography>
											<Typography variant="caption">
												{item.averageDuration || "-"}
											</Typography>
										</div>
									</div>
									<div className="step-content-bottom">
										<Tooltip
											title={
												item.completedDate
													? `Completed by ${item.completedBy}`
													: ""
											}
											arrow
											placement="top"
										>
											<div className="left-part">
												<Typography variant="caption" className="light">
													Date Completed
												</Typography>
												<Typography variant="caption">
													{item.completedDate
														? `${Shared.getFormattedDate(
																new Date(item.completedDate),
																"dd/MM/yyyy"
														  )} - ${Shared.getFormattedDate(
																new Date(item.completedDate),
																"hh:mmaaa"
														  )}`
														: "-"}
												</Typography>
											</div>
										</Tooltip>
										{item.comments.length > 0 && (
											<CustomTooltip
												tooltipStyle={{
													border: "1px solid #034f8b",
												}}
												tooltipProps={{
													title: item.comments.map(
														(cmt: IStepHistoryComment, index: number) => (
															<Grid
																container
																key={index}
																className={index !== 0 ? "history-comment" : ""}
															>
																<Grid item xs={12} className="fw-500">
																	{cmt.fullName}
																</Grid>
																<Grid item xs={12} mt={1}>
																	{cmt.comment}
																</Grid>
															</Grid>
														)
													),
													className: "history-tooltip",
													placement: "right",
													children: (
														<IconButton>
															<CommentIcon />
														</IconButton>
													),
												}}
											/>
										)}
									</div>
								</div>
							</Link>
						)
					)}
			</div>
			{openInfoDialog.open && (
				<ResponsiveDialog
					open={openInfoDialog.open}
					title="Step History Info"
					showActionButtons={false}
					titleCloseIcon={false}
					maxWidth="xs"
					onCancel={() => {}}
				>
					<Grid container spacing={1}>
						<Grid item xs={12}>
							<Typography variant="h4" color="black">
								{openInfoDialog.message}
							</Typography>
						</Grid>
						<Grid
							item
							xs={12}
							textAlign="center"
							sx={{ paddingTop: "24px !important" }}
						>
							<Button
								variant="contained"
								onClick={() => {
									setOpenInfoDialog({ open: false, message: "" });
								}}
							>
								Close
							</Button>
						</Grid>
					</Grid>
				</ResponsiveDialog>
			)}
		</div>
	);
};
